import { errorMsg } from '../../utils/errorMsg';
import React, { useState, useEffect, useCallback } from 'react';
import {
  Container, Typography, Box, Button, TextField, CircularProgress, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions,
  MenuItem, Switch, Select, FormControl, InputLabel, Tabs, Tab
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import FilterListIcon from '@mui/icons-material/FilterList';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../../axios';
import ModuleHeader from '../../components/ModuleHeader';

const TEAL = '#00F1A7';
const GREEN = '#10B981';
const RED = '#EF4444';

const tabSx = {
  mb: 2,
  '& .MuiTab-root': { color: 'text.secondary', fontWeight: 600, textTransform: 'none' },
  '& .Mui-selected': { color: 'text.primary' },
};

function formatCurrency(value) {
  if (value == null) return 'R$ 0,00';
  const num = typeof value === 'number' ? value : parseFloat(value);
  if (isNaN(num)) return 'R$ 0,00';
  return num.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function formatDate(dateStr) {
  if (!dateStr) return '-';
  try {
    const [y, m, d] = dateStr.split('-');
    return `${d}/${m}/${y}`;
  } catch {
    return dateStr;
  }
}

export default function LancamentosFinanceiro({ inTab } = {}) {
  const navigate = useNavigate();
  const location = useLocation();
  const tabPaths = ['/financeiro', '/financeiro/lancamentos', '/financeiro/categorias', '/financeiro/contas'];
  const tabIndex = Math.max(0, tabPaths.indexOf(location.pathname));

  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');
  const [sucesso, setSucesso] = useState('');

  const [lancamentos, setLancamentos] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [centrosCusto, setCentrosCusto] = useState([]);

  const [openModal, setOpenModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const [filtroDataInicio, setFiltroDataInicio] = useState(null);
  const [filtroDataFim, setFiltroDataFim] = useState(null);
  const [filtroTipo, setFiltroTipo] = useState('todos');
  const [filtroCategoria, setFiltroCategoria] = useState('');

  const [formTipo, setFormTipo] = useState('receita');
  const [formDescricao, setFormDescricao] = useState('');
  const [formValor, setFormValor] = useState('');
  const [formData, setFormData] = useState(null);
  const [formDataVencimento, setFormDataVencimento] = useState(null);
  const [formCategoriaId, setFormCategoriaId] = useState('');
  const [formCentroCustoId, setFormCentroCustoId] = useState('');
  const [formContaId, setFormContaId] = useState('');
  const [formObservacao, setFormObservacao] = useState('');

  const carregarCategorias = useCallback(async () => {
    try {
      const res = await api.get('/financeiro/categorias');
      setCategorias(res.data);
    } catch {}
  }, []);

  const carregarCentrosCusto = useCallback(async () => {
    try {
      const res = await api.get('/financeiro/centros-custo');
      setCentrosCusto(res.data);
    } catch {}
  }, []);

  const carregarLancamentos = useCallback(async () => {
    setLoading(true);
    setErro('');
    try {
      const params = {};
      if (filtroDataInicio) params.data_inicio = new Date(filtroDataInicio).toISOString().slice(0, 10);
      if (filtroDataFim) params.data_fim = new Date(filtroDataFim).toISOString().slice(0, 10);
      if (filtroTipo !== 'todos') params.tipo = filtroTipo;
      if (filtroCategoria) params.categoria_id = filtroCategoria;
      const res = await api.get('/financeiro/lancamentos', { params });
      setLancamentos(res.data?.lancamentos || []);
    } catch (err) {
      setErro(errorMsg(err) || 'Erro ao carregar dados');
    }
    setLoading(false);
  }, [filtroDataInicio, filtroDataFim, filtroTipo, filtroCategoria]);

  useEffect(() => {
    carregarCategorias();
    carregarCentrosCusto();
  }, [carregarCategorias, carregarCentrosCusto]);

  useEffect(() => {
    carregarLancamentos();
  }, [carregarLancamentos]);

  const resetForm = () => {
    setEditId(null);
    setFormTipo('receita');
    setFormDescricao('');
    setFormValor('');
    setFormData(null);
    setFormDataVencimento(null);
    setFormCategoriaId('');
    setFormCentroCustoId('');
    setFormContaId('');
    setFormObservacao('');
    setErro('');
    setOpenModal(false);
  };

  const handleSubmit = async () => {
    setErro('');
    if (!formDescricao) { setErro('Preencha a descrição'); return; }
    if (!formValor) { setErro('Preencha o valor'); return; }
    if (!formData) { setErro('Preencha a data'); return; }
    try {
      const payload = {
        tipo: formTipo,
        descricao: formDescricao,
        valor: parseFloat(formValor.replace(',', '.')),
        data: new Date(formData).toISOString().slice(0, 10),
        data_vencimento: formDataVencimento ? new Date(formDataVencimento).toISOString().slice(0, 10) : null,
        categoria_id: formCategoriaId ? parseInt(formCategoriaId, 10) : null,
        centro_custo_id: formCentroCustoId ? parseInt(formCentroCustoId, 10) : null,
        conta_id: formContaId || null,
        observacao: formObservacao || null,
      };
      if (editId) {
        await api.put(`/financeiro/lancamentos/${editId}`, payload);
        setSucesso('Lançamento atualizado!');
      } else {
        await api.post('/financeiro/lancamentos', payload);
        setSucesso('Lançamento criado!');
      }
      resetForm();
      carregarLancamentos();
    } catch (err) {
      setErro(errorMsg(err) || 'Erro ao salvar');
    }
  };

  const handleDelete = async (id) => {
    setErro('');
    try {
      await api.delete(`/financeiro/lancamentos/${id}`);
      setSucesso('Lançamento excluído!');
      setConfirmDelete(null);
      carregarLancamentos();
    } catch (err) {
      setErro(errorMsg(err) || 'Erro ao excluir');
    }
  };

  const handleEdit = (lancamento) => {
    setEditId(lancamento.id);
    setFormTipo(lancamento.tipo || 'receita');
    setFormDescricao(lancamento.descricao || '');
    setFormValor(String(lancamento.valor || ''));
    setFormData(lancamento.data || null);
    setFormDataVencimento(lancamento.data_vencimento || null);
    setFormCategoriaId(lancamento.categoria_id ? String(lancamento.categoria_id) : '');
    setFormCentroCustoId(lancamento.centro_custo_id ? String(lancamento.centro_custo_id) : '');
    setFormContaId(lancamento.conta_id || '');
    setFormObservacao(lancamento.observacao || '');
    setOpenModal(true);
  };

  const handleTogglePago = async (lancamento) => {
    try {
      await api.patch(`/financeiro/lancamentos/${lancamento.id}/pagar`);
      carregarLancamentos();
    } catch (err) {
      setErro(errorMsg(err) || 'Erro ao alterar status');
    }
  };

  const getCategoriaNome = (catId) => {
    const cat = categorias.find(c => c.id === catId);
    return cat ? cat.nome : '-';
  };

  const getCentroCustoNome = (ccId) => {
    const cc = centrosCusto.find(c => c.id === ccId);
    return cc ? cc.nome : '-';
  };

  const aplicarFiltros = () => {
    carregarLancamentos();
  };

  const limparFiltros = () => {
    setFiltroDataInicio(null);
    setFiltroDataFim(null);
    setFiltroTipo('todos');
    setFiltroCategoria('');
  };

  return (
    <>
      <Container maxWidth="lg" sx={inTab ? { px: 0 } : { py: { xs: 2, md: 4 }, px: { xs: 1, md: 2 } }}>
        {!inTab && <ModuleHeader module="Financeiro" title="Lançamentos" />}

        {!inTab && (
          <Tabs
            value={tabIndex}
            onChange={(_, v) => navigate(tabPaths[v])}
            variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile
            TabIndicatorProps={{ sx: { bgcolor: TEAL, height: 2, borderRadius: 2 } }}
            sx={tabSx}
          >
            <Tab label="Dashboard" />
            <Tab label="Lançamentos" />
            <Tab label="Categorias" />
            <Tab label="Contas" />
          </Tabs>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
          {inTab && <Typography variant="h5" sx={{ fontWeight: 700 }}>Lançamentos</Typography>}
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => { resetForm(); setOpenModal(true); }}
            sx={{ bgcolor: TEAL, color: '#fff', ml: 'auto' }}
          >
            Novo Lançamento
          </Button>
        </Box>

        <Paper elevation={0} sx={{ p: 2, mb: 3, borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <FilterListIcon sx={{ color: TEAL }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Filtros</Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <TextField label="Data Início" type="date" size="small" sx={{ minWidth: 150 }} InputLabelProps={{ shrink: true }}
              value={filtroDataInicio || ''} onChange={e => setFiltroDataInicio(e.target.value)} />
            <TextField label="Data Fim" type="date" size="small" sx={{ minWidth: 150 }} InputLabelProps={{ shrink: true }}
              value={filtroDataFim || ''} onChange={e => setFiltroDataFim(e.target.value)} />
            <FormControl size="small" sx={{ minWidth: 130 }}>
              <InputLabel>Tipo</InputLabel>
              <Select value={filtroTipo} label="Tipo" onChange={e => setFiltroTipo(e.target.value)}>
                <MenuItem value="todos">Todos</MenuItem>
                <MenuItem value="receita">Receita</MenuItem>
                <MenuItem value="despesa">Despesa</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Categoria</InputLabel>
              <Select value={filtroCategoria} label="Categoria" onChange={e => setFiltroCategoria(e.target.value)}>
                <MenuItem value="">Todas</MenuItem>
                {categorias.map(cat => (
                  <MenuItem key={cat.id} value={cat.id}>{cat.nome}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button variant="outlined" onClick={aplicarFiltros} sx={{ borderColor: TEAL, color: TEAL, '&:hover': { borderColor: TEAL, bgcolor: 'rgba(139,92,246,0.08)' } }}>
              Filtrar
            </Button>
            <Button variant="text" onClick={limparFiltros} sx={{ color: '#666' }}>
              Limpar
            </Button>
          </Box>
        </Paper>

        {sucesso && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSucesso('')}>{sucesso}</Alert>}
        {erro && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErro('')}>{erro}</Alert>}

        {loading ? (
          <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>
        ) : (
          <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 1, border: '1px solid', borderColor: 'divider', overflowX: 'auto' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Data</TableCell>
                  <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Descrição</TableCell>
                  <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Categoria</TableCell>
                  <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Centro de Custo</TableCell>
                  <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Valor</TableCell>
                  <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }} align="center">Pago</TableCell>
                  <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }} align="center">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {lancamentos.map((lanc) => (
                  <TableRow key={lanc.id} hover sx={{ '&:last-child td': { border: 0 } }}>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{formatDate(lanc.data)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{lanc.descricao}</Typography>
                      {lanc.data_vencimento && (
                        <Typography variant="caption" color="text.secondary">
                          Venc: {formatDate(lanc.data_vencimento)}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{getCategoriaNome(lanc.categoria_id)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{getCentroCustoNome(lanc.centro_custo_id)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 700, color: lanc.tipo === 'receita' ? GREEN : RED }}
                      >
                        {lanc.tipo === 'despesa' ? '- ' : ''}{formatCurrency(lanc.valor)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Switch
                        checked={!!lanc.pago}
                        onChange={() => handleTogglePago(lanc)}
                        size="small"
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': { color: GREEN },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: GREEN },
                        }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                        <Tooltip title="Editar">
                          <IconButton size="small" onClick={() => handleEdit(lanc)} sx={{ color: TEAL }}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Excluir">
                          <IconButton size="small" color="error" onClick={() => setConfirmDelete(lanc.id)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
                {lancamentos.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      Nenhum lançamento encontrado.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Container>

      <Dialog open={openModal} onClose={resetForm} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, borderBottom: `2px solid ${TEAL}` }}>
          {editId ? 'Editar Lançamento' : 'Novo Lançamento'}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Tipo</InputLabel>
              <Select value={formTipo} label="Tipo" onChange={e => setFormTipo(e.target.value)}>
                <MenuItem value="receita">Receita</MenuItem>
                <MenuItem value="despesa">Despesa</MenuItem>
              </Select>
            </FormControl>
            <TextField label="Descrição" value={formDescricao} onChange={e => setFormDescricao(e.target.value)} fullWidth size="small" />
            <TextField label="Valor" value={formValor} onChange={e => setFormValor(e.target.value)} fullWidth size="small" placeholder="0,00" />
            <TextField label="Data" type="date" value={formData || ''} onChange={e => setFormData(e.target.value)} fullWidth size="small" InputLabelProps={{ shrink: true }} />
            <TextField label="Data Vencimento" type="date" value={formDataVencimento || ''} onChange={e => setFormDataVencimento(e.target.value)} fullWidth size="small" InputLabelProps={{ shrink: true }} />
            <FormControl fullWidth size="small">
              <InputLabel>Categoria</InputLabel>
              <Select value={formCategoriaId} label="Categoria" onChange={e => setFormCategoriaId(e.target.value)}>
                <MenuItem value="">Selecione</MenuItem>
                {categorias.map(cat => (
                  <MenuItem key={cat.id} value={cat.id}>{cat.nome}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel>Centro de Custo</InputLabel>
              <Select value={formCentroCustoId} label="Centro de Custo" onChange={e => setFormCentroCustoId(e.target.value)}>
                <MenuItem value="">Selecione</MenuItem>
                {centrosCusto.map(cc => (
                  <MenuItem key={cc.id} value={cc.id}>{cc.nome}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField label="Conta ID" value={formContaId} onChange={e => setFormContaId(e.target.value)} fullWidth size="small" />
            <TextField label="Observação" value={formObservacao} onChange={e => setFormObservacao(e.target.value)} fullWidth size="small" multiline rows={2} />
          </Box>
          {erro && <Alert severity="error" sx={{ mt: 2 }}>{erro}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={resetForm} startIcon={<CloseIcon />}>Cancelar</Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSubmit}
            sx={{ bgcolor: TEAL, color: '#fff', '&:hover': { filter: 'brightness(0.9)' } }}
          >
            {editId ? 'Salvar' : 'Criar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Confirmar exclusão</DialogTitle>
        <DialogContent>
          <Typography>Tem certeza que deseja excluir este lançamento? Esta ação não pode ser desfeita.</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmDelete(null)}>Cancelar</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => handleDelete(confirmDelete)}
          >
            Excluir
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
